import React, { useEffect, useState } from 'react';
import AdminSidebar from '../components/AdminSidebar';
import { db } from '../firebase';
import {
  collection, addDoc, updateDoc, deleteDoc, doc,
  onSnapshot, query, orderBy, serverTimestamp
} from 'firebase/firestore';
import { Plus, Pencil, Trash2, X, Search, Link2, AlertCircle, Users } from 'lucide-react';

const EMPTY = {
  name: '', phone: '', address: '',
  animalId: '',
  distributionStatus: 'pending',
  distributionMethod: 'antar',
  notes: ''
};

const STATUS_COLOR = { pending: 'badge-gray', proses: 'badge-amber', distribusi: 'badge-blue', selesai: 'badge-green' };
const STATUS_LABEL = { pending: 'Menunggu', proses: 'Diproses', distribusi: 'Dikirim', selesai: 'Diterima' };
const ANIMAL_STATUS = { daftar: 'Terdaftar', sembelih: 'Disembelih', packing: 'Dipacking', distribusi: 'Distribusi', selesai: 'Selesai' };

const MAX_PER_ANIMAL = { sapi: 7, kambing: 1 };

export default function AdminDonors() {
  const [donors, setDonors] = useState([]);
  const [animals, setAnimals] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [search, setSearch] = useState('');

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3500); };

  useEffect(() => {
    const u1 = onSnapshot(query(collection(db, 'donors'), orderBy('createdAt', 'desc')), s =>
      setDonors(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    const u2 = onSnapshot(query(collection(db, 'animals'), orderBy('type', 'asc')), s =>
      setAnimals(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    return () => { u1(); u2(); };
  }, []);

  const getAnimal = (animalId) => animals.find(a => a.id === animalId);

  // Hitung jumlah pengkurban yang sudah terdaftar untuk hewan tertentu
  // (exclude donor yang sedang diedit agar slot-nya tidak terhitung ganda)
  const getDonorCount = (animalId) =>
    donors.filter(d => d.animalId === animalId && d.id !== editId).length;

  // Apakah hewan masih bisa menerima pengkurban baru?
  const isAnimalAvailable = (animal) => {
    const count = getDonorCount(animal.id);
    const max = MAX_PER_ANIMAL[animal.type] || 1;
    return count < max;
  };

  // Semua hewan yang masih bisa dipilih (belum penuh)
  const availableAnimals = animals.filter(isAnimalAvailable);

  // Hewan yang sudah full
  const fullAnimals = animals.filter(a => !isAnimalAvailable(a));

  // Hewan yang belum ada pengkurban sama sekali.
  // PENTING: Tidak pakai getDonorCount() di sini karena getDonorCount
  // mengecualikan editId — yang menyebabkan warning tetap muncul
  // meski pengkurban sudah diisi. Kita hitung langsung dari donors.
  const emptyAnimals = animals.filter(a =>
    donors.filter(d => d.animalId === a.id).length === 0
  );

  const openAdd = () => { setForm(EMPTY); setEditId(null); setShowModal(true); };
  const openEdit = (d) => {
    setForm({
      name: d.name || '', phone: d.phone || '', address: d.address || '',
      animalId: d.animalId || '',
      distributionStatus: d.distributionStatus || 'pending',
      distributionMethod: d.distributionMethod || 'antar',
      notes: d.notes || ''
    });
    setEditId(d.id);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name) { showToast('Nama pengkurban wajib diisi', 'error'); return; }

    // Validasi kapasitas hewan
    if (form.animalId) {
      const animal = getAnimal(form.animalId);
      if (animal) {
        const count = getDonorCount(form.animalId);
        const max = MAX_PER_ANIMAL[animal.type] || 1;
        if (count >= max) {
          showToast(`❌ ${animal.type === 'sapi' ? 'Sapi' : 'Kambing'} "${animal.name}" sudah penuh (${max}/${max} pengkurban)`, 'error');
          return;
        }
      }
    }

    setSaving(true);
    try {
      const animal = getAnimal(form.animalId);
      const data = {
        name: form.name, phone: form.phone, address: form.address,
        animalId: form.animalId || '',
        animalName: animal?.name || '',
        animalType: animal?.type || '',
        distributionStatus: form.distributionStatus,
        distributionMethod: form.distributionMethod,
        notes: form.notes,
        updatedAt: serverTimestamp()
      };
      if (editId) {
        await updateDoc(doc(db, 'donors', editId), data);
        showToast('✅ Data pengkurban diperbarui');
      } else {
        await addDoc(collection(db, 'donors'), { ...data, createdAt: serverTimestamp() });
        showToast('✅ Pengkurban berhasil ditambahkan');
      }
      setShowModal(false);
    } catch (e) { showToast('❌ Gagal: ' + e.message, 'error'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus pengkurban ini?')) return;
    await deleteDoc(doc(db, 'donors', id));
    showToast('Dihapus');
  };

  const filtered = donors.filter(d =>
    d.name?.toLowerCase().includes(search.toLowerCase()) ||
    d.address?.toLowerCase().includes(search.toLowerCase()) ||
    d.phone?.includes(search) ||
    d.animalName?.toLowerCase().includes(search.toLowerCase())
  );

  // Slot info untuk preview modal
  const selectedAnimal = getAnimal(form.animalId);
  const selectedAnimalCount = form.animalId ? getDonorCount(form.animalId) : 0;
  const selectedAnimalMax = selectedAnimal ? MAX_PER_ANIMAL[selectedAnimal.type] || 1 : 1;
  const selectedAnimalFull = selectedAnimalCount >= selectedAnimalMax;

  // Ringkasan slot per hewan (untuk tabel ringkasan)
  const animalSummary = animals.map(a => ({
    ...a,
    donorCount: donors.filter(d => d.animalId === a.id).length,
    max: MAX_PER_ANIMAL[a.type] || 1,
    donorList: donors.filter(d => d.animalId === a.id),
  }));

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content">

        <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1>Data Pengkurban</h1>
            <p>Sapi: maks. 7 pengkurban (musytarak) · Kambing: 1 pengkurban</p>
          </div>
          <button className="btn btn-primary" onClick={openAdd}><Plus size={16} /> Tambah Pengkurban</button>
        </div>

        {/* Ringkasan slot hewan */}
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div style={{ fontWeight: 700, color: 'var(--green-900)', fontSize: 14, marginBottom: 12 }}>
            📋 Kapasitas Slot Hewan
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {animalSummary.map(a => {
              const pct = Math.round(a.donorCount / a.max * 100);
              const isFull = a.donorCount >= a.max;
              return (
                <div key={a.id} style={{
                  background: isFull ? '#f0fdf4' : '#fff',
                  border: `1.5px solid ${isFull ? 'var(--green-400)' : 'var(--border)'}`,
                  borderRadius: 12, padding: '10px 14px', minWidth: 170,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <span style={{ fontSize: 18 }}>{a.type === 'sapi' ? '🐄' : '🐏'}</span>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--green-900)' }}>{a.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{a.type === 'sapi' ? 'Sapi · maks 7' : 'Kambing · maks 1'}</div>
                    </div>
                  </div>
                  {/* Slot visual */}
                  <div style={{ display: 'flex', gap: 3, marginBottom: 5 }}>
                    {Array.from({ length: a.max }).map((_, i) => (
                      <div key={i} style={{
                        flex: 1, height: 8, borderRadius: 4,
                        background: i < a.donorCount ? 'var(--green-500)' : '#e2e8f0',
                        transition: 'background 0.2s'
                      }} />
                    ))}
                  </div>
                  <div style={{ fontSize: 12, color: isFull ? '#16a34a' : 'var(--text-muted)', fontWeight: isFull ? 700 : 400 }}>
                    {isFull ? '✅ Penuh' : `${a.donorCount}/${a.max} pengkurban`}
                  </div>
                  {/* Nama-nama pengkurban */}
                  {a.donorList.length > 0 && (
                    <div style={{ marginTop: 5, borderTop: '1px solid var(--border)', paddingTop: 5 }}>
                      {a.donorList.map((d, i) => (
                        <div key={d.id} style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.6 }}>
                          {i + 1}. {d.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {animals.length === 0 && (
              <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                Belum ada data hewan. Tambahkan di menu <strong>Hewan Qurban</strong> dulu.
              </div>
            )}
          </div>
        </div>

        {/* Alert hewan belum punya pengkurban */}
        {emptyAnimals.length > 0 && (
          <div style={{ display: 'flex', gap: 10, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12, padding: '12px 16px', marginBottom: '1.25rem', fontSize: 13, color: '#92400e' }}>
            <AlertCircle size={15} style={{ flex: 'none', marginTop: 1 }} />
            <span>
              <strong>{emptyAnimals.length} hewan belum ada pengkurban:</strong>{' '}
              {emptyAnimals.map(a => `${a.type === 'sapi' ? '🐄' : '🐏'} ${a.name}`).join(', ')}
            </span>
          </div>
        )}

        {/* Stats */}
        <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)', marginBottom: '1.25rem' }}>
          {[
            { label: 'Total Pengkurban', value: donors.length, color: 'var(--green-800)' },
            { label: 'Diterima', value: donors.filter(d => d.distributionStatus === 'selesai').length, color: '#16a34a' },
            { label: 'Dikirim', value: donors.filter(d => d.distributionStatus === 'distribusi').length, color: '#0369a1' },
            { label: 'Menunggu', value: donors.filter(d => !d.distributionStatus || d.distributionStatus === 'pending').length, color: '#d97706' },
            { label: 'Slot Sapi Tersisa', value: animals.filter(a => a.type === 'sapi').reduce((acc, a) => acc + Math.max(0, 7 - donors.filter(d => d.animalId === a.id).length), 0), color: '#7c3aed' },
          ].map((s, i) => (
            <div key={i} className="stat-card">
              <div className="stat-label">{s.label}</div>
              <div className="stat-value" style={{ color: s.color, fontSize: 22 }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* Search */}
        <div style={{ position: 'relative', marginBottom: '1rem', maxWidth: 380 }}>
          <Search size={15} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input className="form-input" placeholder="Cari nama, hewan, alamat..."
            style={{ paddingLeft: 34 }} value={search} onChange={e => setSearch(e.target.value)} />
        </div>

        {/* Table */}
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>No</th><th>Nama Pengkurban</th><th>Telepon</th><th>Alamat</th>
                <th>Hewan Qurban</th><th>Slot</th><th>Proses Hewan</th>
                <th>Cara Terima</th><th>Status Distribusi</th><th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d, i) => {
                const animal = getAnimal(d.animalId);
                const slotCount = animal ? donors.filter(x => x.animalId === animal.id).length : 0;
                const slotMax = animal ? MAX_PER_ANIMAL[animal.type] || 1 : 1;
                const aStatus = animal?.status;
                return (
                  <tr key={d.id}>
                    <td style={{ color: 'var(--text-muted)', fontWeight: 600 }}>{i + 1}</td>
                    <td style={{ fontWeight: 600 }}>{d.name}</td>
                    <td style={{ fontSize: 13 }}>{d.phone || '-'}</td>
                    <td style={{ fontSize: 13, maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.address || '-'}</td>
                    <td>
                      {animal ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span>{animal.type === 'sapi' ? '🐄' : '🐏'}</span>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>{animal.name}</div>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                              {[animal.weight && `${animal.weight}kg`, animal.color].filter(Boolean).join(' · ')}
                            </div>
                          </div>
                          <Link2 size={11} color="var(--green-500)" />
                        </div>
                      ) : (
                        <span style={{ fontSize: 12, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 3 }}>
                          <AlertCircle size={12} /> Belum dipilih
                        </span>
                      )}
                    </td>
                    <td>
                      {animal && (
                        <div style={{ fontSize: 12 }}>
                          <div style={{ display: 'flex', gap: 2, marginBottom: 2 }}>
                            {Array.from({ length: slotMax }).map((_, idx) => (
                              <div key={idx} style={{ width: 8, height: 8, borderRadius: 2, background: idx < slotCount ? 'var(--green-500)' : '#e2e8f0' }} />
                            ))}
                          </div>
                          <div style={{ color: 'var(--text-muted)' }}>{slotCount}/{slotMax}</div>
                        </div>
                      )}
                    </td>
                    <td>
                      {animal ? (
                        <span className={`badge ${aStatus === 'selesai' ? 'badge-green' : aStatus === 'distribusi' ? 'badge-blue' : aStatus === 'packing' ? 'badge-amber' : aStatus === 'sembelih' ? 'badge-red' : 'badge-gray'}`}>
                          {ANIMAL_STATUS[aStatus] || '-'}
                        </span>
                      ) : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>-</span>}
                    </td>
                    <td><span className="badge badge-gray">{d.distributionMethod === 'ambil' ? '🏠 Ambil' : '🚚 Antar'}</span></td>
                    <td><span className={`badge ${STATUS_COLOR[d.distributionStatus] || 'badge-gray'}`}>{STATUS_LABEL[d.distributionStatus] || 'Menunggu'}</span></td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn btn-sm btn-ghost" onClick={() => openEdit(d)} style={{ padding: '4px 8px' }}><Pencil size={13} /></button>
                        <button className="btn btn-sm btn-danger" onClick={() => handleDelete(d.id)} style={{ padding: '4px 8px' }}><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={10} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                  {search ? `Tidak ada hasil untuk "${search}"` : 'Belum ada data pengkurban'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Modal */}
        {showModal && (
          <div className="modal-overlay">
            <div className="modal" style={{ maxWidth: 560 }}>
              <div className="modal-header">
                <div className="modal-title">{editId ? '✏️ Edit Pengkurban' : '➕ Tambah Pengkurban'}</div>
                <button className="modal-close" onClick={() => setShowModal(false)}><X size={18} /></button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1rem' }}>

                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label className="form-label">Nama Lengkap Pengkurban *</label>
                  <input className="form-input" placeholder="Ahmad Fauzi bin Abdullah"
                    value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
                </div>

                <div className="form-group">
                  <label className="form-label">Nomor Telepon</label>
                  <input type="tel" className="form-input" placeholder="081234567890"
                    value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} />
                </div>

                <div className="form-group">
                  <label className="form-label">Cara Terima Daging</label>
                  <select className="form-input form-select" value={form.distributionMethod}
                    onChange={e => setForm(p => ({ ...p, distributionMethod: e.target.value }))}>
                    <option value="antar">🚚 Diantar Kurir</option>
                    <option value="ambil">🏠 Ambil Sendiri</option>
                  </select>
                </div>

                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label className="form-label">Alamat Lengkap</label>
                  <input className="form-input"
                    placeholder="Jl. Merdeka No. 10, RT 02/RW 03, Kel. Wonokromo, Surabaya"
                    value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} />
                </div>

                {/* Dropdown pilih hewan — dengan info slot */}
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label className="form-label">Pilih Hewan Qurban</label>
                  <select className="form-input form-select" value={form.animalId}
                    onChange={e => setForm(p => ({ ...p, animalId: e.target.value }))}>
                    <option value="">-- Pilih hewan yang dikurbankan --</option>

                    {/* Hewan saat ini (edit mode) */}
                    {editId && form.animalId && !availableAnimals.find(a => a.id === form.animalId) && (() => {
                      const cur = getAnimal(form.animalId);
                      return cur ? (
                        <option value={cur.id}>
                          {cur.type === 'sapi' ? '🐄 Sapi' : '🐏 Kambing'} — {cur.name} (saat ini)
                        </option>
                      ) : null;
                    })()}

                    {/* Sapi tersedia */}
                    {availableAnimals.filter(a => a.type === 'sapi').length > 0 && (
                      <optgroup label="🐄 Sapi — masih ada slot">
                        {availableAnimals.filter(a => a.type === 'sapi').map(a => {
                          const c = getDonorCount(a.id);
                          const sisa = 7 - c;
                          return (
                            <option key={a.id} value={a.id}>
                              {a.name}{a.weight ? ` · ${a.weight}kg` : ''}{a.color ? ` · ${a.color}` : ''} — slot {c}/7 (sisa {sisa})
                            </option>
                          );
                        })}
                      </optgroup>
                    )}

                    {/* Kambing tersedia */}
                    {availableAnimals.filter(a => a.type === 'kambing').length > 0 && (
                      <optgroup label="🐏 Kambing — masih tersedia">
                        {availableAnimals.filter(a => a.type === 'kambing').map(a => (
                          <option key={a.id} value={a.id}>
                            {a.name}{a.weight ? ` · ${a.weight}kg` : ''}{a.color ? ` · ${a.color}` : ''}
                          </option>
                        ))}
                      </optgroup>
                    )}

                    {/* Hewan penuh */}
                    {fullAnimals.length > 0 && (
                      <optgroup label="🔒 Sudah penuh">
                        {fullAnimals.map(a => {
                          const max = MAX_PER_ANIMAL[a.type];
                          return (
                            <option key={a.id} value={a.id} disabled>
                              {a.type === 'sapi' ? '🐄' : '🐏'} {a.name} — PENUH ({max}/{max})
                            </option>
                          );
                        })}
                      </optgroup>
                    )}

                    {availableAnimals.length === 0 && !form.animalId && (
                      <option disabled>Semua hewan sudah penuh</option>
                    )}
                  </select>

                  {/* Preview hewan terpilih */}
                  {selectedAnimal && (
                    <div style={{
                      marginTop: 8, padding: '12px 14px',
                      background: selectedAnimalFull ? '#fef2f2' : 'var(--green-50)',
                      border: `1px solid ${selectedAnimalFull ? '#fca5a5' : 'var(--green-200)'}`,
                      borderRadius: 10
                    }}>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                        <span style={{ fontSize: 28 }}>{selectedAnimal.type === 'sapi' ? '🐄' : '🐏'}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700, color: 'var(--green-900)', fontSize: 14 }}>{selectedAnimal.name}</div>
                          <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 1 }}>
                            {[selectedAnimal.type === 'sapi' ? 'Sapi' : 'Kambing', selectedAnimal.weight && `${selectedAnimal.weight}kg`, selectedAnimal.color, selectedAnimal.age && `${selectedAnimal.age} thn`].filter(Boolean).join(' · ')}
                          </div>

                          {/* Slot bar */}
                          <div style={{ marginTop: 8 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                              <Users size={12} color="var(--text-muted)" />
                              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                                Pengkurban: {selectedAnimalCount}/{selectedAnimalMax}
                                {selectedAnimal.type === 'sapi' && ` (sisa ${selectedAnimalMax - selectedAnimalCount} slot)`}
                              </span>
                            </div>
                            <div style={{ display: 'flex', gap: 4 }}>
                              {Array.from({ length: selectedAnimalMax }).map((_, i) => (
                                <div key={i} style={{
                                  flex: 1, height: 10, borderRadius: 4,
                                  background: i < selectedAnimalCount
                                    ? (i === selectedAnimalCount - 1 && !editId ? '#fbbf24' : 'var(--green-500)')
                                    : (i === selectedAnimalCount && !editId ? '#86efac' : '#e2e8f0'),
                                  transition: 'background 0.2s'
                                }} />
                              ))}
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>
                              {Array.from({ length: selectedAnimalMax }).map((_, i) => {
                                const donorAtSlot = donors.filter(d => d.animalId === selectedAnimal.id && d.id !== editId)[i];
                                return donorAtSlot ? (
                                  <span key={i} style={{ marginRight: 6 }}>{i+1}. {donorAtSlot.name}</span>
                                ) : null;
                              })}
                            </div>
                          </div>

                          {selectedAnimalFull && !editId && (
                            <div style={{ marginTop: 6, color: '#dc2626', fontSize: 12, fontWeight: 600 }}>
                              ❌ Hewan ini sudah penuh! Pilih hewan lain.
                            </div>
                          )}
                          {!selectedAnimalFull && !editId && (
                            <div style={{ marginTop: 6, color: '#16a34a', fontSize: 12, fontWeight: 600 }}>
                              ✅ Masih ada slot — pengkurban ini akan jadi ke-{selectedAnimalCount + 1}
                            </div>
                          )}
                        </div>
                        <Link2 size={14} color="var(--green-600)" />
                      </div>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Status Distribusi</label>
                  <select className="form-input form-select" value={form.distributionStatus}
                    onChange={e => setForm(p => ({ ...p, distributionStatus: e.target.value }))}>
                    <option value="pending">⏳ Menunggu</option>
                    <option value="proses">🔄 Diproses</option>
                    <option value="distribusi">🚚 Sedang Dikirim</option>
                    <option value="selesai">✅ Sudah Diterima</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Catatan</label>
                  <input className="form-input" placeholder="Titip ke tetangga, dll..."
                    value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
                </div>

              </div>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
                <button className="btn btn-ghost" onClick={() => setShowModal(false)}>Batal</button>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving || (selectedAnimalFull && !editId)}>
                  {saving ? '⏳ Menyimpan...' : 'Simpan'}
                </button>
              </div>
            </div>
          </div>
        )}

        {toast && <div className="toast-container"><div className={`toast toast-${toast.type}`}>{toast.msg}</div></div>}
      </div>
    </div>
  );
}
